import { zodResolver } from "@hookform/resolvers/zod";
import { FunctionComponent, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { FetchBaseQueryError } from "@reduxjs/toolkit/query";

import { NavLink } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "@/redux/hooks";

import FormTextField from "@/pages/Private/pages/Account/components/FormTextField";

import FormContainer from "../../components/FormContainer";
import { useAuthTwoFactorCodeMutation, useSendEmailOtpMutation } from "../../redux/auth.api";
import {
	authSelector,
	backTo2FA,
	clearOtpToken,
	setPendingEmailOtpUserId,
} from "../../redux/auth.slice";
import { TwoFactorAuthSchema } from "./schema/twoFactorAuth";

export const TwoFactorAuth: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`twoFA.${key}`);

	const { otpToken, user } = useAppSelector(authSelector);
	const storedOtpToken = sessionStorage.getItem("otpToken");
	const effectiveOtpToken = otpToken || storedOtpToken;
	const userId = user?.id ?? Number(sessionStorage.getItem("pendingTwoFaUserId"));
	const [sendEmailOtp, { isLoading: isSendingOtp }] = useSendEmailOtpMutation();
	const dispatch = useAppDispatch();

	const handleUseEmailOtp = async () => {
		if (!userId) {
			return;
		}

		await sendEmailOtp({ userId }).unwrap();
		dispatch(setPendingEmailOtpUserId(userId)); // this already clears otpToken from state
	};

	useEffect(() => {
		if (!otpToken && storedOtpToken) {
			dispatch(backTo2FA());
		}
	}, []);

	const [auth, { isLoading, error: authError }] = useAuthTwoFactorCodeMutation();
	const {
		handleSubmit,
		control,
		formState: { errors },
	} = useForm({
		defaultValues: {
			code: "",
		},
		resolver: zodResolver(TwoFactorAuthSchema),
	});

	const onSubmit = async ({ code }: { code: string }) => {
		try {
			await auth({ code, token: effectiveOtpToken ?? "" }).unwrap();
		} catch (err) {
			console.error(err);
		}
	};

	const errorMessage =
		errors?.code?.message ||
		(authError as FetchBaseQueryError & { data?: { message: string } })?.data?.message;

	return (
		<div className="w-full h-full flex items-center justify-center">
			<FormContainer
				description={ts("description")}
				formFooter={
					<div className="w-full flex justify-end items-center">
						<NavLink
							className="bb-link text-[14px] text-nowrap"
							to="#"
							onClick={(e) => {
								e.preventDefault();
								handleUseEmailOtp();
							}}
						>
							{isSendingOtp ? t("basics.loading") : t("twoFA.useEmailOtp")}
						</NavLink>
					</div>
				}
				handleSubmit={handleSubmit(onSubmit)}
				isLoading={isLoading}
				submitButtonText={ts("cta")}
				title={ts("title")}
			>
				<Controller
					control={control}
					name="code"
					render={({ field }) => (
						<FormTextField
							{...field}
							autoComplete="off"
							errorMessage={errorMessage}
							label={ts("code")}
						/>
					)}
				/>
			</FormContainer>
		</div>
	);
};
